export type LeadStatus =
  | "Initial"
  | "Pending"
  | "Success"
  | "Failure"
  | "Not_Supported";

export interface Lead {
  id: number;
  name: string;
  email: string;
  phone: string;
  websiteSharedUid: string;
  brandUid: string;
  brand: string;
  status: LeadStatus;
  industry: string;
  loanPurpose: string;
  amount: string;
  revenue: string;
  credit: string;
  timeInBusiness: string;
  zip: string;
  businessName: string;
  userUniqueId: string;
  trafficSource: string;
  adId: string;
  adPlatformClickId: string;
  date: string;
  reportedAt: string | null;
  created: string;
  modified: string;
}

// The Leads Management screen is currently scoped to this single site. Its
// website_shared_uid differs per environment (staging/production don't share
// a database), but the site_name is stable across environments, so access
// and the shared_uid to query with are both resolved from it at runtime.
export const LEADS_MANAGEMENT_SITE_NAME = "Opti Fund";

export const STATUS_ORDER: LeadStatus[] = [
  "Pending",
  "Initial",
  "Success",
  "Failure",
  "Not_Supported",
];

export const STATUS_META: Record<
  LeadStatus,
  { label: string; color: string; bg: string }
> = {
  Initial: { label: "Initial", color: "#1565C0", bg: "#E3F2FD" },
  Pending: { label: "Pending", color: "#E65100", bg: "#FFF3E0" },
  Success: { label: "Success", color: "#2E7D32", bg: "#E8F5E9" },
  Failure: { label: "Failure", color: "#C62828", bg: "#FFEBEE" },
  Not_Supported: { label: "Not Supported", color: "#616161", bg: "#F0F0F0" },
};

export const BRAND_COLOR = "#8E24AA";

export function isReportableStatus(status: LeadStatus): boolean {
  return status === "Pending";
}

export function formatLeadDateTime(iso: string | null): string {
  if (!iso) return "—";
  const date = new Date(iso);
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  let hours = date.getHours();
  const meridiem = hours < 12 ? "a.m." : "p.m.";
  hours = hours % 12 || 12;
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}, ${hours}:${minutes} ${meridiem}`;
}

function toIsoDateString(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Leads Management defaults to the last 30 days. */
export function getDefaultDateRange(referenceDate: Date = new Date()): {
  from: string;
  to: string;
} {
  return {
    from: toIsoDateString(new Date(referenceDate.getTime() - 30 * 86400000)),
    to: toIsoDateString(referenceDate),
  };
}

export const MAX_DATE_RANGE_DAYS = 365;

/** The latest end date the picker should allow for a given start date, in yyyy-mm-dd form. */
export function getMaxEndDate(start: string): string | undefined {
  if (!start) return undefined;
  const startDate = new Date(`${start}T00:00:00`);
  if (Number.isNaN(startDate.getTime())) return undefined;
  return toIsoDateString(new Date(startDate.getTime() + MAX_DATE_RANGE_DAYS * 86400000));
}

/** The earliest start date the picker should allow for a given end date, in yyyy-mm-dd form. */
export function getMinStartDate(end: string): string | undefined {
  if (!end) return undefined;
  const endDate = new Date(`${end}T00:00:00`);
  if (Number.isNaN(endDate.getTime())) return undefined;
  return toIsoDateString(new Date(endDate.getTime() - MAX_DATE_RANGE_DAYS * 86400000));
}

/** True when end is before start, or more than MAX_DATE_RANGE_DAYS after it (both set). */
export function isDateRangeInvalid(start: string, end: string): boolean {
  if (!start || !end) return false;
  const startDate = new Date(`${start}T00:00:00`);
  const endDate = new Date(`${end}T00:00:00`);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return false;
  const daysBetween = (endDate.getTime() - startDate.getTime()) / 86400000;
  return daysBetween < 0 || daysBetween > MAX_DATE_RANGE_DAYS;
}

/** Maps a single record from POST /partners/brand_leads_report/ into a Lead. */
export function mapApiLeadToLead(record: any): Lead {
  return {
    id: record.id,
    name: record.full_name,
    email: record.email,
    phone: record.phone,
    websiteSharedUid: record.website_shared_uid,
    brandUid: record.brand_uid,
    brand: record.brand_name,
    status: record.report_status,
    industry: record.business_industry,
    loanPurpose: record.loan_purpose,
    amount: record.borrow_amount,
    revenue: record.monthly_revenue,
    credit: record.credit_rating,
    timeInBusiness: record.time_in_business,
    zip: record.zip_code,
    businessName: record.business_name,
    userUniqueId: record.user_unique_id,
    trafficSource: record.traffic_source,
    adId: record.adid,
    adPlatformClickId: record.ad_platform_click_id,
    date: record.date_of_acquisition,
    reportedAt: record.reported_at,
    created: record.created,
    modified: record.modified,
  };
}

export interface LeadsAggregations {
  byStatus: Array<{ status: LeadStatus; count: number }>;
  byDay: Array<{ date: string; count: number }>;
  byBrand: Array<{ brandUid: string; brandName: string; count: number }>;
}

export const EMPTY_AGGREGATIONS: LeadsAggregations = {
  byStatus: [],
  byDay: [],
  byBrand: [],
};

/** Maps the `aggregations` object from POST /partners/brand_leads_report/. */
export function mapApiAggregations(aggregations: any): LeadsAggregations {
  return {
    byStatus: (aggregations?.by_status ?? []).map((item: any) => ({
      status: item.report_status,
      count: item.count,
    })),
    byDay: (aggregations?.by_day ?? []).map((item: any) => ({
      date: item.date,
      count: item.count,
    })),
    byBrand: (aggregations?.by_brand ?? []).map((item: any) => ({
      brandUid: item.brand_uid,
      brandName: item.brand_name,
      count: item.count,
    })),
  };
}

/**
 * Checks whether the logged-in user (from /users/me/, stored as selectUserData)
 * has access to the site with the given site_name. Mirrors the lookup in
 * src/customHooks/useWebsiteSharedUid.ts.
 */
export function userHasWebsiteAccess(
  userData: any,
  siteName: string,
): boolean {
  const websitePermissions = userData?.data?.website_permissions || [];
  return websitePermissions.some((permission: any) => {
    const details: any = Object.values(permission)[0];
    return details?.site_name === siteName;
  });
}
